import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'

import theme from '@/theme'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import {
  setActiveMenuItem,
  setTemporaryActiveItem,
  setSubmenuState,
  setHoveredMenuItem,
} from '@/redux/ducks/menu/main'
import { isMobileScreen } from '@/modules/_helpers/detectMobile'
import { makeStyles } from '@mui/styles'
import { MenuItems } from '@/modules/base/util/constants'
import { InputLoader } from '@/modules/base/components/inputs'
import staffService from '@/modules/_services/staffService'
import { MenuItemIcons } from '@/modules/layout/components/menuV2/menu-bar/menu-bar-item'
import openStartTrialModal from '@/modules/base/components/modal/_helpers/openStartTrialModal'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'
import { useNavigate } from 'react-router'
import { useAppSelector } from '@/redux/store'
import { isMeinBuero } from '@/utils/build-env'

const ICON_DIMENSIONS = { height: '20px', width: '20px' }

const hovered = {
  background: theme.navMenuItemBackgroundColor,
}

const useStyles = makeStyles(theme => ({
  loader: {
    background: theme.navBackgroundColor,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    height: 80,
    minHeight: 80,
    borderLeft: '3px solid transparent',
    color: theme.mobileNav.inactiveMenuItem,
    fill: theme.mobileNav.inactiveMenuItem,
    width: '100%',
    fontSize: '18px',
    cursor: 'pointer',
    '&:hover': {
      ...hovered,
      borderLeftColor: 'transparent',
    },
  },
  active: {
    ...hovered,
    '&$hasSubmenu': {
      borderLeftColor: 'var(--nav-menu-item-border-color-active)',
      '&:after': {
        display: 'block',
      },
    },
  },
  hasSubmenu: {
    // defining dummy object required for $ selector
  },
  link: {
    width: '100%',
    fontSize: '18px',
  },
  title: {
    color: 'var(--nav-submenu-item-color)',
    fontSize: '11px',
    lineHeight: '11px',
    marginTop: '8px',
  },
  icon: {
    height: 20,
    width: 20,
  },
  [theme.devices.mobile]: {
    container: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      minHeight: 'unset',
      height: 'auto',
      border: 'none',
      padding: '15px 20px',
    },
    active: {
      background: 'unset',
      border: 'unset',
      color: theme.mobileNav.activeMenuItem,
      fontWeight: theme.fontWeights.medium,
    },
    title: {
      fontSize: theme.fontSizes.smedium,
      color: 'inherit',
      margin: '0 0 0 16px',
    },
  },
  expired: {
    color: theme.disabledColor,
    '& $icon *': {
      fill: theme.disabledColor,
    },
  },
}))

const isMB = isMeinBuero()

const MenuItem = props => {
  const navigate = useNavigate()
  const {
    title = '',
    name,
    url = '',
    submenuItems,
    isIncludedInPlan,
    requiredSub,
    handleItemClick,
    dataQsId,
  } = props
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const openUpgradeModal = useUpgradeModal()
  const { subscriptions } = useAppSelector(state => state.subscription?.details)
  const notTrialSub = useMemo(() => subscriptions.find((sub) => !sub.isTrial), [subscriptions])

  const activeItem = useSelector(state => state.menu.main.activeItem)
  const [activeMenuItem] = activeItem.split('/')

  const isActive = activeMenuItem === name
  const classes = useStyles()
  const activeClass = isActive ? classes.active : ''
  const hasSubmenuClass = submenuItems.length > 0 ? classes.hasSubmenu : ''
  const fillColor = isMobileScreen() ? isActive ? theme.mobileNav.demoHeader : theme.white : theme.white

  const handleMobileMenuClick = async() => {
    if (requiredSub) {
      const { exists, isExpired, type } = requiredSub
      if (!exists) {
        return openStartTrialModal({
          subscriptionType: type,
        })
      }

      if (isExpired) {
        return openUpgradeModal({ subscriptionType: type })
      }
    }

    if (name === MenuItems.Staff) {
      setIsLoading(true)
      await staffService.openPage()
      setIsLoading(false)
    } else if (!isMB && name === MenuItems.Marketplace && !notTrialSub) {
      openUpgradeModal()
    } else if (!submenuItems?.length) {
      dispatch(setActiveMenuItem(name))
      navigate(url)
    } else if (isIncludedInPlan) {
      dispatch(setHoveredMenuItem(name))
      dispatch(setTemporaryActiveItem(name))
      dispatch(setSubmenuState(true))
      handleItemClick()
    }
  }

  const rootClass = clsx(
    classes.container,
    activeClass,
    hasSubmenuClass,
    (requiredSub && (requiredSub.isExpired || !requiredSub.exists)) && classes.expired,
  )
  const menuItemEl = (
    <a
      className={classes.link}
      onClick={handleMobileMenuClick}
      data-qs-id={dataQsId}
    >
      <div className={rootClass}>
        <SVGIcon {...ICON_DIMENSIONS} svg={MenuItemIcons[name]} className={classes.icon} fill={fillColor} />
        <div className={classes.title}>{title}</div>
      </div>
    </a>
  )

  return isLoading ? <InputLoader customClass={clsx(classes.loader, classes.container)} /> : menuItemEl
}

MenuItem.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string,
  icon: PropTypes.string,
  active: PropTypes.bool,
}

export default MenuItem
